import { Card, Flex, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

import { useEffect, useState } from "react";

import { FILTER_KEYS, SORTER_KEYS } from "constants/table";
import { FIELD_KEYS, FIELD_ORDERS, TABLE_FIELDS } from "constants/UnitFields";
import {
  NumberRangeArrayParam,
  SorterArrayParam,
} from "functions/queryParamFuncs";
import AdvancedSearchButton from "components/Common/AdvancedSearchButton";
import AdvancedSearchModal from "components/ItemDetails/AdvancedSearchModal";
import CardTitle from "components/Common/CardTitle";
import Pagination from "components/Common/Pagination";
import UnitTable from "components/ItemDetails/UnitTable";
import useFetch from "hooks/useFetch";
import useExport from "hooks/useExport";

// ##### Functions #####

const getFilterCount = (filters) => {
  return Object.entries(filters).reduce((acc, [key, val]) => {
    let filterCount = 0;

    if (key === FILTER_KEYS.SORTERS) {
      filterCount = Array.isArray(val)
        ? val.filter(
            (v) => !!v?.[SORTER_KEYS.SORT_BY] && !!v?.[SORTER_KEYS.SORT_ORDER]
          ).length
        : 0;
    } else if (
      ![FILTER_KEYS.PAGE, FILTER_KEYS.PAGE_SIZE, "item"].includes(key)
    ) {
      const isFiltered =
        val !== null &&
        val !== undefined &&
        val !== "" &&
        (!Array.isArray(val) || val.length > 0);
      filterCount = isFiltered ? 1 : 0;
    }

    return acc + filterCount;
  }, 0);
};

// ##### Component #####

const UnitListCard = ({ itemUuid }) => {
  const {
    loading,
    responseData: unitListResponseData,
    doFetch: getUnitList,
  } = useFetch("/units/getUnitList", "GET", []);
  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);
  const [filters, setFilters] = useState({
    item: itemUuid,
    [FIELD_KEYS.X]: null,
    [FIELD_KEYS.STATUS]: null,
    [FIELD_KEYS.DETECTED_CODE]: null,
    [FILTER_KEYS.PAGE]: 1,
    [FILTER_KEYS.PAGE_SIZE]: 10,
    [FILTER_KEYS.SORTERS]: null,
  });
  const {
    responseData: responseDataForExport,
    doFetch: getUnitListForExport,
    doExport: exportToCSV,
  } = useExport("/units/getUnitList", "GET", [])

  const tableData = unitListResponseData.data ?? [];
  const tableDataTotalCount = unitListResponseData.total_count ?? 0;
  const exportData = responseDataForExport.data ?? [];

  const filterCount = getFilterCount(filters);

  const updateFilters = (newFilters) => {
    setFilters({
      ...filters,
      ...newFilters,
    });
  };

  const fetchUnitList = () => {
    getUnitList({
      ...filters,
      [FIELD_KEYS.X]: NumberRangeArrayParam.encodeApi(filters[FIELD_KEYS.X]),
      [FILTER_KEYS.SORTERS]: SorterArrayParam.encodeApi(
        filters[FILTER_KEYS.SORTERS]
      ),
    });
  };

  const fetchUnitListForExport = () => {
    getUnitListForExport({
      ...filters,
      [FILTER_KEYS.PAGE_SIZE]: tableDataTotalCount,
      [FIELD_KEYS.X]: NumberRangeArrayParam.encodeApi(filters[FIELD_KEYS.X]),
      [FILTER_KEYS.SORTERS]: SorterArrayParam.encodeApi(
        filters[FILTER_KEYS.SORTERS]
      ),
    })
  }

  useEffect(() => {
    if (!!filters.item) {
      fetchUnitList();
    }
  }, [filters]);

  useEffect(()=>{
    if(exportData.length > 0){
      exportToCSV(exportData, "Unit list", TABLE_FIELDS, FIELD_ORDERS)
    }
  },[exportData])

  return (
    <>
      <Card>
        <CardTitle title="List of Units" />
        <Flex vertical={true} gap="middle" wrap="wrap">
          <Flex justify="space-between" gap="0.5rem 0" wrap="wrap">
            <Flex gap="small" wrap="wrap">
              <AdvancedSearchButton
                filterCount={filterCount}
                loading={loading}
                onClick={() => setShowAdvancedSearchModal(true)}
              />
              <Button
                disabled={loading}
                onClick={()=>{fetchUnitListForExport()}}
                icon={<DownloadOutlined />}
                type="text"
              />
            </Flex>
            
            <Pagination
              currentPage={filters[FILTER_KEYS.PAGE]}
              currentPageSize={filters[FILTER_KEYS.PAGE_SIZE]}
              totalCount={tableDataTotalCount}
              loading={loading}
              onChange={(page, pageSize) => {
                updateFilters({
                  [FILTER_KEYS.PAGE]: page,
                  [FILTER_KEYS.PAGE_SIZE]: pageSize,
                });
              }}
            />
          </Flex>

          <UnitTable tableData={tableData} loading={loading} />
          <Flex justify="end" wrap="wrap">
            <Pagination
              currentPage={filters[FILTER_KEYS.PAGE]}
              currentPageSize={filters[FILTER_KEYS.PAGE_SIZE]}
              totalCount={tableDataTotalCount}
              loading={loading}
              onChange={(page, pageSize) => {
                updateFilters({
                  [FILTER_KEYS.PAGE]: page,
                  [FILTER_KEYS.PAGE_SIZE]: pageSize,
                });
              }}
            />
          </Flex>
        </Flex>
      </Card>
      <AdvancedSearchModal
        showModal={showAdvancedSearchModal}
        filters={filters}
        onShowModalChange={setShowAdvancedSearchModal}
        onFiltersChange={updateFilters}
      />
    </>
  );
};

export default UnitListCard;
